import {
  Box,
  Button,
  Container,
  Flex,
  HStack,
  Heading,
  Input,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import type React from "react"
import { useMemo, useState } from "react"
import { FiDownload, FiPrinter, FiSearch } from "react-icons/fi"
import { z } from "zod"

import { PrintableAttendanceSheet } from "@/components/PrintableAttendanceSheet"
import { AttendanceActionsMenu } from "@/components/layout/Common/AttendanceActionsMenu"
import { AttendanceCard } from "@/components/molecules/AttendanceCard"
import { type ViewMode, ViewToggle } from "@/components/molecules/ViewToggle"
import {
  DataTable,
  type DataTableColumn,
} from "@/components/organisms/DataTable"
import { AddAttendance } from "@/features/attendances"
import { useClassTemplates } from "@/hooks/api/useClassTemplates"
import { useSessions } from "@/hooks/api/useSessions"
import { usePermissions } from "@/hooks/usePermissions"
import { useIsMobile } from "@/hooks/useResponsive"
import { api } from "@/services/api"
import type {
  AttendanceListResponse,
  AttendanceResponse,
} from "@/types/attendance"

type AttendanceStatus =
  | "present"
  | "absent"
  | "excused"
  | "late"
  | "makeup"
  | "cancelled"
import { unwrapStandardResponse } from "@/utils/apiResponse"
import { type ExportColumn, exportToExcel } from "@/utils/exportToExcel"

const attendancesSearchSchema = z.object({
  page: z.coerce.number().min(1).catch(1),
  search: z.string().optional(),
  sort: z.enum(["created_at", "updated_at"]).catch("created_at"),
  direction: z.enum(["asc", "desc"]).catch("desc"),
  status: z.string().optional(),
  class_template_id: z.string().optional(),
  session_id: z.string().optional(),
  class_date_from: z.string().optional(),
  class_date_to: z.string().optional(),
})

const PER_PAGE = 25
type SortField = "created_at" | "updated_at"
type SortDirection = "asc" | "desc"

const ATTENDANCE_STATUSES = [
  "present",
  "absent",
  "excused",
  "late",
  "makeup",
  "cancelled",
]

const formatFieldValue = (value: unknown, type: string): string => {
  if (value === null || value === undefined || value === "") {
    return "N/A"
  }

  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : "N/A"
  }

  if (type === "bool") {
    if (typeof value === "string") {
      if (value.toLowerCase() === "true") return "Yes"
      if (value.toLowerCase() === "false") return "No"
      return value
    }
    return value ? "Yes" : "No"
  }

  if (type === "datetime" || type === "date" || type === "time") {
    const dateValue = new Date(String(value))
    if (!Number.isNaN(dateValue.getTime())) {
      if (type === "date") return dateValue.toLocaleDateString()
      if (type === "time") return dateValue.toLocaleTimeString()
      return dateValue.toLocaleString()
    }
  }

  if (typeof value === "object") {
    try {
      return JSON.stringify(value)
    } catch (error) {
      return "N/A"
    }
  }

  return String(value)
}

function getAttendancesQueryOptions({
  page,
  search,
  sort,
  direction,
  status,
  classTemplateId,
  sessionId,
  classDateFrom,
  classDateTo,
}: {
  page: number
  search?: string
  sort: SortField
  direction: SortDirection
  status?: string
  classTemplateId?: string
  sessionId?: string
  classDateFrom?: string
  classDateTo?: string
}) {
  const debugAttendances =
    import.meta.env.DEV && import.meta.env.VITE_DEBUG_ATTENDANCES === "true"

  return {
    queryFn: async () => {
      if (debugAttendances) {
        console.log(
          `Attendances page: Fetching page ${page} with ${PER_PAGE} records per page`,
        )
      }

      const response = await api.attendances.readAttendancesApiV1AttendancesGet(
        {
          skip: (page - 1) * PER_PAGE,
          limit: PER_PAGE,
          search,
          sortBy: sort,
          sortOrder: direction,
          status: status as AttendanceStatus | undefined,
          classTemplateId,
          sessionId,
          classDateFrom,
          classDateTo,
        },
      )

      const payload =
        unwrapStandardResponse<AttendanceListResponse>(response)
      const items = payload?.items ?? []
      const total = payload?.total ?? 0

      if (debugAttendances) {
        console.log("Attendances page: API payload:", payload)
        console.log("Attendances page: items count:", items.length)
        console.log("Attendances page: total:", total)
      }

      return {
        data: items,
        count: total,
      }
    },
    queryKey: [
      "attendances",
      {
        page,
        search,
        sort,
        direction,
        status,
        classTemplateId,
        sessionId,
        classDateFrom,
        classDateTo,
      },
    ],
  }
}

export const Route = createFileRoute("/_layout/attendances")({
  component: Attendances,
  validateSearch: (search) => attendancesSearchSchema.parse(search),
})

const selectStyle = {
  borderRadius: "md",
  borderWidth: "1px",
  borderColor: "gray.200",
  px: 3,
  py: 1,
  fontSize: "sm",
  bg: "white",
  _hover: { borderColor: "gray.300" },
  _focus: {
    borderColor: "blue.500",
    outline: "none",
    boxShadow: "0 0 0 1px rgba(66, 153, 225, 0.6)",
  },
}

function Attendances() {
  const navigate = useNavigate({ from: Route.fullPath })
  const {
    page,
    search,
    sort,
    direction,
    status: statusFilter,
    class_template_id,
    session_id,
    class_date_from,
    class_date_to,
  } = Route.useSearch()
  const { hasPermission } = usePermissions()
  const [searchInput, setSearchInput] = useState(search ?? "")
  const [viewMode, setViewMode] = useState<ViewMode>("table")
  const isMobile = useIsMobile()
  const effectiveViewMode = isMobile ? "cards" : viewMode

  const { data: sessionsData } = useSessions({ limit: 100 })
  const sessions = sessionsData?.items ?? []

  const { data: classTemplatesData } = useClassTemplates({ limit: 100 })
  const classTemplates = classTemplatesData?.items ?? []

  const hasActiveFilters = !!(
    statusFilter ||
    class_template_id ||
    session_id ||
    class_date_from ||
    class_date_to
  )

  const isStaffOrAdmin =
    hasPermission("attendances:update") || hasPermission("attendances:delete")

  const { data, isLoading, isPlaceholderData } = useQuery({
    ...getAttendancesQueryOptions({
      page,
      search: search || undefined,
      sort,
      direction,
      status: statusFilter,
      classTemplateId: class_template_id,
      sessionId: session_id,
      classDateFrom: class_date_from,
      classDateTo: class_date_to,
    }),
    placeholderData: (previous) => previous,
  })

  const setPage = (nextPage: number) =>
    navigate({
      search: (previous) => ({
        ...previous,
        page: nextPage,
      }),
    })

  const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const nextValue = searchInput.trim()
    navigate({
      search: (previous) => {
        const { search: _prevSearch, ...rest } = previous
        return nextValue
          ? { ...rest, page: 1, search: nextValue }
          : { ...rest, page: 1 }
      },
    })
  }

  const handleSortChange = (nextSort: SortField) => {
    navigate({
      search: (previous) => ({
        ...previous,
        page: 1,
        sort: nextSort,
      }),
    })
  }

  const handleDirectionChange = (nextDirection: SortDirection) => {
    navigate({
      search: (previous) => ({
        ...previous,
        page: 1,
        direction: nextDirection,
      }),
    })
  }

  const handleFilterChange = (key: string, value: string) => {
    navigate({
      search: (previous) => {
        const next: Record<string, unknown> = { ...previous, page: 1 }
        if (value) {
          next[key] = value
        } else {
          delete next[key]
        }
        return next as typeof previous
      },
    })
  }

  const clearFilters = () => {
    navigate({
      search: (previous) => {
        const {
          status: _,
          class_template_id: __,
          session_id: ___,
          class_date_from: ____,
          class_date_to: _____,
          ...rest
        } = previous
        return { ...rest, page: 1 }
      },
    })
  }

  const records = data?.data?.slice(0, PER_PAGE) ?? []
  const count = data?.count ?? 0
  const showActions =
    hasPermission("attendances:update") || hasPermission("attendances:delete")

  // Export columns
  const attendanceExportColumns: ExportColumn<AttendanceResponse>[] = [
    {
      header: "Student",
      accessor: (att) =>
        att.student_name || att.student_id || "N/A",
    },
    {
      header: "Class",
      accessor: (att) => att.class_name || "N/A",
    },
    {
      header: "Date",
      accessor: (att) =>
        att.class_date
          ? new Date(att.class_date).toLocaleDateString()
          : "N/A",
    },
    {
      header: "Time",
      accessor: (att) => att.class_time || "N/A",
    },
    {
      header: "Status",
      accessor: (att) => att.status || "Not marked",
    },
    {
      header: "Notes",
      accessor: (att) => att.instructor_notes || "",
    },
  ]

  const handleExport = () => {
    const today = new Date().toISOString().slice(0, 10)
    exportToExcel(records, attendanceExportColumns, `attendance-${today}`, "Attendance")
  }

  const handlePrint = () => {
    window.print()
  }

  const printFilters = {
    className: class_template_id
      ? classTemplates.find((ct) => ct.id === class_template_id)?.name
      : undefined,
    dateFrom: class_date_from,
    dateTo: class_date_to,
    sessionName: session_id
      ? sessions.find((s) => s.id === session_id)?.name
      : undefined,
  }

  const columns = useMemo<DataTableColumn<AttendanceResponse>[]>(() => {
    const tableColumns: DataTableColumn<AttendanceResponse>[] = []

    tableColumns.push({
      key: "student_name",
      label: "Student",
      render: (attendance) => (
        <Text fontWeight="medium">
          {attendance.student_name ||
            formatFieldValue(attendance.student_id, "str")}
        </Text>
      ),
    })

    tableColumns.push({
      key: "class_name",
      label: "Class",
      render: (attendance) => (
        <Text>{attendance.class_name || "N/A"}</Text>
      ),
    })

    tableColumns.push({
      key: "class_date",
      label: "Date",
      render: (attendance) => (
        <Text>{formatFieldValue(attendance.class_date, "date")}</Text>
      ),
    })

    tableColumns.push({
      key: "class_time",
      label: "Time",
      render: (attendance) => (
        <Text>{formatFieldValue(attendance.class_time, "str")}</Text>
      ),
    })

    tableColumns.push({
      key: "status",
      label: "Status",
      render: (attendance) => {
        const status = attendance.status || "Not marked"
        const colorScheme =
          status === "present"
            ? "green"
            : status === "absent"
              ? "red"
              : status === "excused"
                ? "blue"
                : status === "late"
                  ? "orange"
                  : "gray"

        return (
          <Box
            as="span"
            px={2}
            py={1}
            borderRadius="md"
            fontSize="xs"
            fontWeight="medium"
            bg={`${colorScheme}.50`}
            color={`${colorScheme}.700`}
          >
            {status}
          </Box>
        )
      },
    })

    tableColumns.push({
      key: "instructor_notes",
      label: "Notes",
      render: (attendance) => (
        <Text truncate maxW="200px" title={attendance.instructor_notes || ""}>
          {formatFieldValue(attendance.instructor_notes, "str")}
        </Text>
      ),
    })

    if (showActions) {
      tableColumns.push({
        key: "actions",
        label: "Actions",
        render: (attendance) => (
          <AttendanceActionsMenu attendance={attendance} />
        ),
      })
    }

    return tableColumns
  }, [showActions])

  if (isLoading) {
    return (
      <Container maxW="full">
        <Flex justifyContent="space-between" alignItems="center" pt={12} mb={6}>
          <Heading size="lg">Attendance Records</Heading>
        </Flex>
        <Flex
          alignItems="center"
          justifyContent="center"
          direction="column"
          gap={4}
          minH="240px"
        >
          <Spinner size="lg" color="blue.500" />
          <Text color="gray.600">Loading attendances...</Text>
        </Flex>
      </Container>
    )
  }

  return (
    <Container maxW="full">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-only { display: block !important; margin-bottom: 16px; }
          nav, header, aside { display: none !important; }
          table { font-size: 10px; }
        }
      `}</style>
      <Flex
        className="no-print"
        direction={{ base: "column", md: "row" }}
        justify="space-between"
        align={{ base: "stretch", md: "center" }}
        pt={12}
        mb={6}
        gap={{ base: 3, md: 0 }}
      >
        <Flex justify="space-between" align="center">
          <Heading size="lg">Attendance Records</Heading>
          {hasPermission("attendances:create") && (
            <Box display={{ base: "block", md: "none" }}>
              <AddAttendance />
            </Box>
          )}
        </Flex>
        <Flex
          direction={{ base: "column", md: "row" }}
          gap={{ base: 3, md: 4 }}
          align={{ base: "stretch", md: "center" }}
        >
          <form onSubmit={handleSearchSubmit}>
            <HStack gap={2}>
              <Input
                value={searchInput}
                onChange={(event) => setSearchInput(event.target.value)}
                placeholder="Search attendance records"
                size="sm"
                width={{ base: "full", md: "220px" }}
                aria-label="Search attendance records"
              />
              <Button type="submit" size="sm" colorPalette="blue">
                Search
              </Button>
            </HStack>
          </form>
          <HStack gap={2}>
            <Box
              {/* TODO: Chakra UI v3 polymorphic type limitation - as any required */ ...({} as any)}
              as="select"
              size="sm"
              value={sort}
              onChange={(event) =>
                handleSortChange(
                  (event.target as HTMLSelectElement).value as SortField,
                )
              }
              aria-label="Sort records"
              width={{ base: "full", md: "170px" }}
              flex={{ base: 1, md: "none" }}
              {...selectStyle}
            >
              <option value="created_at">Newest</option>
              <option value="updated_at">Recently updated</option>
              <option value="name">Name</option>
            </Box>
            <Box
              {/* TODO: Chakra UI v3 polymorphic type limitation - as any required */ ...({} as any)}
              as="select"
              size="sm"
              value={direction}
              onChange={(event) =>
                handleDirectionChange(
                  (event.target as HTMLSelectElement).value as SortDirection,
                )
              }
              aria-label="Sort direction"
              width={{ base: "full", md: "150px" }}
              flex={{ base: 1, md: "none" }}
              {...selectStyle}
            >
              <option value="desc">Descending</option>
              <option value="asc">Ascending</option>
            </Box>
          </HStack>
          <Box display={{ base: "none", md: "flex" }}>
            <HStack gap={4}>
              <ViewToggle value={viewMode} onChange={setViewMode} />
              {isStaffOrAdmin && (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handleExport}
                    disabled={records.length === 0}
                  >
                    <HStack gap={1}>
                      <FiDownload />
                      <Text>Export</Text>
                    </HStack>
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={handlePrint}
                    disabled={records.length === 0}
                  >
                    <HStack gap={1}>
                      <FiPrinter />
                      <Text>Print Sheet</Text>
                    </HStack>
                  </Button>
                </>
              )}
              {hasPermission("attendances:create") && <AddAttendance />}
            </HStack>
          </Box>
        </Flex>
      </Flex>

      {/* Filter dropdowns */}
      <Flex
        className="no-print"
        direction={{ base: "column", md: "row" }}
        gap={{ base: 3, md: 3 }}
        mb={4}
        align={{ base: "stretch", md: "center" }}
        flexWrap="wrap"
      >
        <Box
          {/* TODO: Chakra UI v3 polymorphic type limitation - as any required */ ...({} as any)}
          as="select"
          size="sm"
          value={statusFilter ?? ""}
          onChange={(event) =>
            handleFilterChange(
              "status",
              (event.target as HTMLSelectElement).value,
            )
          }
          aria-label="Filter by status"
          width={{ base: "full", md: "160px" }}
          {...selectStyle}
        >
          <option value="">All Statuses</option>
          {ATTENDANCE_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </option>
          ))}
        </Box>
        <Box
          {/* TODO: Chakra UI v3 polymorphic type limitation - as any required */ ...({} as any)}
          as="select"
          size="sm"
          value={class_template_id ?? ""}
          onChange={(event) =>
            handleFilterChange(
              "class_template_id",
              (event.target as HTMLSelectElement).value,
            )
          }
          aria-label="Filter by class"
          width={{ base: "full", md: "200px" }}
          {...selectStyle}
        >
          <option value="">All Classes</option>
          {classTemplates.map((ct) => (
            <option key={ct.id} value={ct.id}>
              {ct.name}
            </option>
          ))}
        </Box>
        <Box
          {/* TODO: Chakra UI v3 polymorphic type limitation - as any required */ ...({} as any)}
          as="select"
          size="sm"
          value={session_id ?? ""}
          onChange={(event) =>
            handleFilterChange(
              "session_id",
              (event.target as HTMLSelectElement).value,
            )
          }
          aria-label="Filter by season"
          width={{ base: "full", md: "200px" }}
          {...selectStyle}
        >
          <option value="">All Seasons</option>
          {sessions.map((session) => (
            <option key={session.id} value={session.id}>
              {session.name}
            </option>
          ))}
        </Box>
        <HStack gap={2}>
          <Input
            type="date"
            size="sm"
            value={class_date_from ?? ""}
            onChange={(e) =>
              handleFilterChange("class_date_from", e.target.value)
            }
            placeholder="From date"
            aria-label="Class date from"
            width={{ base: "full", md: "160px" }}
          />
          <Input
            type="date"
            size="sm"
            value={class_date_to ?? ""}
            onChange={(e) =>
              handleFilterChange("class_date_to", e.target.value)
            }
            placeholder="To date"
            aria-label="Class date to"
            width={{ base: "full", md: "160px" }}
          />
        </HStack>
        {hasActiveFilters && (
          <Button size="sm" variant="ghost" onClick={clearFilters}>
            Clear Filters
          </Button>
        )}
      </Flex>

      {/* Active filter badges */}
      {hasActiveFilters && (
        <HStack className="no-print" gap={2} mb={4} flexWrap="wrap">
          {statusFilter && (
            <Box
              px={2}
              py={1}
              bg="blue.50"
              color="blue.700"
              borderRadius="md"
              fontSize="xs"
            >
              Status: {statusFilter}
            </Box>
          )}
          {class_template_id && (
            <Box
              px={2}
              py={1}
              bg="blue.50"
              color="blue.700"
              borderRadius="md"
              fontSize="xs"
            >
              Class:{" "}
              {classTemplates.find((ct) => ct.id === class_template_id)
                ?.name ?? "Selected"}
            </Box>
          )}
          {session_id && (
            <Box
              px={2}
              py={1}
              bg="blue.50"
              color="blue.700"
              borderRadius="md"
              fontSize="xs"
            >
              Season:{" "}
              {sessions.find((s) => s.id === session_id)?.name ??
                "Selected"}
            </Box>
          )}
          {class_date_from && (
            <Box
              px={2}
              py={1}
              bg="blue.50"
              color="blue.700"
              borderRadius="md"
              fontSize="xs"
            >
              From: {class_date_from}
            </Box>
          )}
          {class_date_to && (
            <Box
              px={2}
              py={1}
              bg="blue.50"
              color="blue.700"
              borderRadius="md"
              fontSize="xs"
            >
              To: {class_date_to}
            </Box>
          )}
        </HStack>
      )}

      {/* Print header - only visible when printing */}
      <Box className="print-only" display="none">
        <Heading size="md">Attendance Records</Heading>
        {hasActiveFilters && (
          <Text fontSize="sm" color="gray.600">
            {[
              statusFilter && `Status: ${statusFilter}`,
              class_template_id &&
                `Class: ${classTemplates.find((ct) => ct.id === class_template_id)?.name}`,
              class_date_from && `From: ${class_date_from}`,
              class_date_to && `To: ${class_date_to}`,
            ]
              .filter(Boolean)
              .join(" | ")}
          </Text>
        )}
      </Box>

      <PrintableAttendanceSheet records={records} filters={printFilters} />

      {records.length === 0 ? (
        <VStack gap={4} py={12} alignItems="center">
          <FiSearch size={48} color="gray" />
          <VStack textAlign="center" gap={2}>
            <Heading size="md" color="gray.600">
              {hasActiveFilters
                ? "No attendance records match your filters"
                : "No attendance records yet"}
            </Heading>
            <Text color="gray">
              {hasActiveFilters
                ? "Try adjusting your filters"
                : hasPermission("attendances:create")
                  ? "Add a new attendance to get started"
                  : "Attendance will appear here once your swimmers attend class meetings."}
            </Text>
          </VStack>
        </VStack>
      ) : (
        <>
          <Box opacity={isPlaceholderData ? 0.5 : 1}>
            {effectiveViewMode === "cards" ? (
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} gap={4}>
                {records.map((attendance: AttendanceResponse) => (
                  <AttendanceCard
                    key={attendance.id}
                    attendance={attendance}
                    showActions={showActions}
                  />
                ))}
              </SimpleGrid>
            ) : (
              <DataTable columns={columns} data={records} size="sm" />
            )}
          </Box>
          <Flex
            className="no-print"
            justifyContent={{ base: "center", md: "flex-end" }}
            mt={4}
            gap={2}
          >
            <Button
              size="sm"
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
            >
              Previous
            </Button>
            <Button size="sm" variant="outline" disabled>
              Page {page} of {Math.ceil(count / PER_PAGE)}
            </Button>
            <Button
              size="sm"
              onClick={() =>
                setPage(Math.min(Math.ceil(count / PER_PAGE), page + 1))
              }
              disabled={page >= Math.ceil(count / PER_PAGE)}
            >
              Next
            </Button>
          </Flex>
        </>
      )}
    </Container>
  )
}
